import {
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';

const ContactSection: React.FC = () => {
  const { showSuccess, showWarning } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      showWarning('Lütfen tüm zorunlu alanları doldurun.', 'Eksik Bilgi');
      return;
    }

    showSuccess(
      'Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.',
      'Teşekkürler'
    );

    setFormData({
      name: '',
      email: '',
      phone: '',
      message: '',
    });
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Başlık */}
        <Box textAlign="center" mb={5}>
          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            sx={{
              background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1.5,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
            }}
          >
            Bize Ulaşın
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: '600px',
              mx: 'auto',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
            }}
          >
            Sorularınız ve talepleriniz için bizimle iletişime geçebilirsiniz
          </Typography>
        </Box>

        {/* Ana İçerik - 2 Kolon */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '500px 1fr' },
            gap: 4,
            mb: 6,
          }}
        >
          {/* Sol Taraf - İletişim Bilgileri */}
          <Box>
            <Card
              elevation={0}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  gutterBottom
                  sx={{ color: '#2E7D32', mb: 4 }}
                >
                  İletişim Bilgileri
                </Typography>

                {/* Telefon */}
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background:
                          'linear-gradient(135deg, #43A047 0%, #66BB6A 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(67, 160, 71, 0.3)',
                      }}
                    >
                      <PhoneIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ fontSize: '0.85rem' }}
                      >
                        Telefon
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        +90 (535) 395 91 49
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* E-posta */}
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background:
                          'linear-gradient(135deg, #2E7D32 0%, #43A047 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                      }}
                    >
                      <EmailIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ fontSize: '0.85rem' }}
                      >
                        E-posta
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        faruk@dietaro.com
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Adres */}
                <Box mb={3}>
                  <Box display="flex" alignItems="flex-start" gap={2} mb={1}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background:
                          'linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(56, 142, 60, 0.3)',
                      }}
                    >
                      <LocationIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ fontSize: '0.85rem' }}
                      >
                        Adres
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        Yalova İş Merkezi
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Gazıpaşa Mahallesi, Cumhuriyet Caddesi No: 15/3
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Yalova Merkez, 77100
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Çalışma Saatleri */}
                <Box
                  sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: 2,
                    background:
                      'linear-gradient(135deg, #f5f7fa 0%, #e8f5e9 100%)',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background:
                          'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ScheduleIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700}>
                      Çalışma Saatleri
                    </Typography>
                  </Box>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Pazartesi - Cuma</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        08:00 - 18:00
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Cumartesi</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        09:00 - 16:00
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Pazar</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="error"
                      >
                        Kapalı
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Sağ Taraf - İletişim Formu */}
          <Box>
            <Card
              elevation={0}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 3,
                background: 'white',
                height: '100%',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  gutterBottom
                  sx={{ color: '#2E7D32', mb: 1 }}
                >
                  Mesaj Gönderin
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 4 }}
                >
                  Formu doldurun, en kısa sürede size geri dönüş yapalım
                </Typography>

                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  display="flex"
                  flexDirection="column"
                  gap={3}
                >
                  <TextField
                    fullWidth
                    required
                    label="Ad Soyad"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Adınız ve soyadınız"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#43A047',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#43A047',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="E-posta"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="faruk@dietaro.com"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#43A047',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#43A047',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Telefon"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+90 (535) 395 91 49"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#43A047',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#43A047',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={5}
                    label="Mesajınız"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder="Mesajınızı buraya yazın..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#43A047',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#43A047',
                        },
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={<SendIcon />}
                    sx={{
                      py: 1.75,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background:
                        'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
                      boxShadow: '0 4px 12px rgba(67, 160, 71, 0.3)',
                      '&:hover': {
                        background:
                          'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
                        boxShadow: '0 6px 16px rgba(67, 160, 71, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Mesaj Gönder
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Harita - Tam Genişlik */}
        <Card
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(67, 160, 71, 0.2)',
              borderColor: '#43A047',
            },
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
              color: 'white',
              p: 2.5,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              📍 Yalova İş Merkezi
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, opacity: 0.95, fontSize: '0.9rem' }}
            >
              Gazıpaşa Mahallesi, Cumhuriyet Caddesi No: 15/3, Yalova Merkez
            </Typography>
          </Box>
          <Box sx={{ height: '400px', position: 'relative' }}>
            <iframe
              src="https://maps.google.com/maps?q=40.6551,29.2767+(Yalova+İş+Merkezi)&z=16&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Yalova İş Merkezi"
            />
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default ContactSection;
